import scaling from './scaling'

const VariablesModule = {
  state: {
    /* Changed by the interface */
    groundSurface: 0,
    flatRoofSurface: 0,
    monoPitchedRoofSurface: 0,
    gableRoofSurface: 0,
    yearlyEnergyUsage: 0,
    dayPrice: 0.05,
    nightPrice: 0.04,
    connectionType: 'large',
    /* Hardcoded values */
    flatRoofPitch: 0,
    groundPitch: 0,
    monoPitchedRoofPitch: 30,
    gableRoofPitch: 45,
    wpPerSolarPanel: 300,
    outputRatioKwhOverNominalPower: 0.85,
    inputSalesPriceNominalPower: 0.9,
    inputPhaseSubsidySDECorrection: 0.063,
    configuredVariableTransportationCost: 0.0089,
    // Zone 1: up to 10.000 kWh
    // Zone 2: from  10.000 kWh to  50.000 kWh
    // Zone 3: from  50.000 kWh to 100.000 kWh
    // Zone 4: from 100.000 kWh to infinity
    configuredEnergyTaxesZoneOne: 0.10458,
    configuredEnergyTaxesZoneTwo: 0.05274,
    configuredEnergyTaxesZoneThree: 0.01404,
    configuredEnergyTaxesZoneFour: 0.00057,
    configuredOdeZoneOne: 0.01320,
    configuredOdeZoneTwo: 0.01800,
    configuredOdeZoneThree: 0.00480,
    configuredOdeZoneFour: 0.00019,
    configuredZoneOneMaximum: 10000,
    configuredZoneTwoMaximum: 50000,
    configuredZoneThreeMaximum: 100000,

    // Company
    salutation: 'Dhr.',
    first_name: '',
    last_name: '',
    company_name: '',
    branche: '',
    email: '',
    phone: ''
  },
  getters: {
    detailsFormValid: (state, getters) => {
      if (state.first_name &&
          state.last_name &&
          state.company_name &&
          state.branche &&
          state.email &&
          state.phone &&
          getters.validEmail) {
        return true
      }
      return false
    },
    validEmail: (state, getters) => {
      var input = document.createElement('input')
      input.type = 'email'
      input.required = true
      input.value = state.email
      return input.validity.valid
    },
    totalSurfaceInMeters: (state, getters) => {
      return getters.groundSurfaceInSquareMeters + state.flatRoofSurface + state.gableRoofSurface + state.monoPitchedRoofSurface
    },
    groundSurfaceInSquareMeters: (state, getters) => {
      // Convert from ha2 to m2
      return state.groundSurface * 10000
    },
    numberOfSolarPanels: (state, getters) => {
      return (
        Math.floor((getters.groundSurfaceInSquareMeters / 2.5)) +
        Math.floor((state.flatRoofSurface / 2.5)) +
        Math.floor((state.gableRoofSurface / 1.6)) +
        Math.floor((state.monoPitchedRoofSurface / 1.6))
      )
    },
    outputNominalPower: (state, getters) => {
      return getters.numberOfSolarPanels * state.wpPerSolarPanel
    },
    outputProduction: (state, getters) => {
      return getters.outputNominalPower * state.outputRatioKwhOverNominalPower
    },
    outputPercentageProductionOverUsage: (state, getters) => {
      return getters.outputProduction / state.yearlyEnergyUsage * 100
    },
    outputEnergyProductionWithinConsumption: (state, getters) => {
      return Math.min(state.yearlyEnergyUsage, getters.outputProduction)
    },
    outputAveragePrice: (state, getters) => {
      return (state.dayPrice * 5 / 7) + (state.nightPrice * 2 / 7)
    },
    outputSalesPrice: (state, getters) => {
      return state.inputSalesPriceNominalPower * getters.outputNominalPower
    },
    outputReservationConverterPerYear: (state, getters) => {
      return (getters.outputNominalPower * 0.06) / 10
    },
    outputPhaseSubsidySDE: (state, getters) => {
      return (getters.outputNominalPower >= 1000000 ? 0.099 : 0.106)
    },
    outputSDEIncomeYearOne: (state, getters) => {
      return (getters.outputPhaseSubsidySDE - state.inputPhaseSubsidySDECorrection) * getters.outputProduction
    },
    outputFeedInReward: (state, getters) => {
      return Math.max(0, getters.outputProduction - state.yearlyEnergyUsage) * 0.035
    },
    outputProductionWithinUsage: (state, getters) => {
      return (state.yearlyEnergyUsage < getters.outputProduction) ? state.yearlyEnergyUsage : getters.outputProduction
    },
    energyUsageZones: (state, getters) => {
      let tempEnergyLeftToDistribute = state.yearlyEnergyUsage
      let tempEnergyUsageZoneOne = Math.min(tempEnergyLeftToDistribute, state.configuredZoneOneMaximum)
      tempEnergyLeftToDistribute -= tempEnergyUsageZoneOne
      let tempEnergyUsageZoneTwo = Math.min(tempEnergyLeftToDistribute, state.configuredZoneTwoMaximum - state.configuredZoneOneMaximum)
      tempEnergyLeftToDistribute -= tempEnergyUsageZoneTwo
      let tempEnergyUsageZoneThree = Math.min(tempEnergyLeftToDistribute, state.configuredZoneThreeMaximum - state.configuredZoneTwoMaximum)
      tempEnergyLeftToDistribute -= tempEnergyUsageZoneThree
      let tempEnergyUsageZoneFour = tempEnergyLeftToDistribute

      return [tempEnergyUsageZoneOne, tempEnergyUsageZoneTwo, tempEnergyUsageZoneThree, tempEnergyUsageZoneFour]
    },
    savingZones: (state, getters) => {
      let tempEnergySavingsLeftToDistribute = getters.outputProductionWithinUsage
      let outputEnergySavingZoneFour = Math.min(tempEnergySavingsLeftToDistribute, getters.energyUsageZones[3]) // [P11]
      tempEnergySavingsLeftToDistribute -= outputEnergySavingZoneFour
      let outputEnergySavingZoneThree = Math.min(tempEnergySavingsLeftToDistribute, getters.energyUsageZones[2]) // [P10]
      tempEnergySavingsLeftToDistribute -= outputEnergySavingZoneThree
      let outputEnergySavingZoneTwo = Math.min(tempEnergySavingsLeftToDistribute, getters.energyUsageZones[1]) // [P9]
      tempEnergySavingsLeftToDistribute -= outputEnergySavingZoneTwo
      let outputEnergySavingZoneOne = Math.min(tempEnergySavingsLeftToDistribute, getters.energyUsageZones[0]) // [P8]

      let outputTaxSavingZoneOne = Math.round(outputEnergySavingZoneOne * state.configuredEnergyTaxesZoneOne) // [Q8]
      let outputTaxSavingZoneTwo = Math.round(outputEnergySavingZoneTwo * state.configuredEnergyTaxesZoneTwo) // [Q9]
      let outputTaxSavingZoneThree = Math.round(outputEnergySavingZoneThree * state.configuredEnergyTaxesZoneThree) // [Q10]
      let outputTaxSavingZoneFour = Math.round(outputEnergySavingZoneFour * state.configuredEnergyTaxesZoneFour) // [Q11]

      let outputOdeSavingZoneOne = Math.round(outputEnergySavingZoneOne * state.configuredOdeZoneOne) // [R8]
      let outputOdeSavingZoneTwo = Math.round(outputEnergySavingZoneTwo * state.configuredOdeZoneTwo) // [R9]
      let outputOdeSavingZoneThree = Math.round(outputEnergySavingZoneThree * state.configuredOdeZoneThree) // [R10]
      let outputOdeSavingZoneFour = Math.round(outputEnergySavingZoneFour * state.configuredOdeZoneFour) // [R11]

      let outputSummedEnergySavingZoneTotal = (
        outputEnergySavingZoneOne +
        outputEnergySavingZoneTwo +
        outputEnergySavingZoneThree +
        outputEnergySavingZoneFour
      ) // [P12]
      let outputTaxSavingZoneTotal = (
        outputTaxSavingZoneOne +
        outputTaxSavingZoneTwo +
        outputTaxSavingZoneThree +
        outputTaxSavingZoneFour
      ) // [Q12]
      let outputOdeSavingZoneTotal = (
        outputOdeSavingZoneOne +
        outputOdeSavingZoneTwo +
        outputOdeSavingZoneThree +
        outputOdeSavingZoneFour
      )

      return [outputSummedEnergySavingZoneTotal, outputTaxSavingZoneTotal, outputOdeSavingZoneTotal]
    },
    outputTaxSavingOverProductionWithinConsumption: (state, getters) => {
      return getters.savingZones[1] / getters.outputEnergyProductionWithinConsumption
    },
    outputOdeSavingOverProductionWithinConsumption: (state, getters) => {
      return getters.savingZones[2] / getters.outputEnergyProductionWithinConsumption
    },
    outputAverageEnergyTaxes: (state, getters) => {
      return getters.outputTaxSavingOverProductionWithinConsumption
    },
    outputAverageOde: (state, getters) => {
      return getters.outputOdeSavingOverProductionWithinConsumption
    },
    outputElectricityPriceIncludingTaxes: (state, getters) => {
      let value = getters.outputAverageOde + getters.outputAverageEnergyTaxes + getters.outputAveragePrice
      if (state.connectionType === 'large') {
        value += state.configuredVariableTransportationCost
      }
      return value
    },
    outputEvadedEnergyBill: (state, getters) => {
      return getters.outputEnergyProductionWithinConsumption * getters.outputElectricityPriceIncludingTaxes
    },
    outputTotalIncomeYearOne: (state, getters) => {
      let value = getters.outputEvadedEnergyBill + getters.outputFeedInReward
      if (state.connectionType === 'large') {
        value += getters.outputSDEIncomeYearOne
      }
      return value
    },
    outputTimeToRecover: (state, getters) => {
      return getters.outputSalesPrice / getters.outputTotalIncomeYearOne
    },
    outputReturnOnInvestment: (state, getters) => {
      return getters.outputTotalIncomeYearOne / getters.outputSalesPrice
    },
    getInputMin: (state, getters) => (value) => {
      return scaling[value].min
    },
    getInputMax: (state, getters) => (value) => {
      return scaling[value].max
    },
    getInputStep: (state, getters) => (value) => {
      return scaling[value].step
    },
    getUnitType: (state, getters) => (value) => {
      return scaling[value].unit
    }
  },
  mutations: {
    setGroundSurface (state, value) {
      state.groundSurface = value
    },
    setFlatRoofSurface (state, value) {
      state.flatRoofSurface = value
    },
    setMonoPitchedRoofSurface (state, value) {
      state.monoPitchedRoofSurface = value
    },
    setGableRoofSurface (state, value) {
      state.gableRoofSurface = value
    },
    setYearlyEnergyUsage (state, value) {
      state.yearlyEnergyUsage = value
    },
    setDayPrice (state, value) {
      state.dayPrice = value
    },
    setNightPrice (state, value) {
      state.nightPrice = value
    },
    setConnectionType (state, value) {
      state.connectionType = value
    },
    setFirstName (state, value) {
      state.first_name = value
    },
    setLastName (state, value) {
      state.last_name = value
    },
    setCompanyName (state, value) {
      state.company_name = value
    },
    setEmail (state, value) {
      state.email = value
    },
    setPhone (state, value) {
      state.phone = value
    },
    setBranche (state, value) {
      state.branche = value
    },
    setSalutation (state, value) {
      state.salutation = value
    }
  }
}

export default VariablesModule



// WEBPACK FOOTER //
// ./src/stores/variables.js